import * as XLSX from "xlsx";

const COLLAPSIBLE_VALUE_COLUMNS = [
  "Value_long",
  "Value_dynamic",
  "Value_string",
  "Value",
  "value",
];

const TYPED_VALUE_SOURCE_COLUMNS = new Set([
  "Value_long",
  "Value_dynamic",
  "Value_string",
  "value",
]);

const IDENTIFIER_VALUE_COLUMNS = new Set(["Metric", "DwellType"]);

const ACRONYM_OVERRIDES = new Map([
  ["id", "ID"],
  ["ids", "IDs"],
  ["api", "API"],
  ["csv", "CSV"],
  ["exec", "Exec"],
  ["kpi", "KPI"],
  ["lph", "LPH"],
  ["uom", "UOM"],
  ["uph", "UPH"],
  ["url", "URL"],
  ["wms", "WMS"],
]);

const VIEW_LABEL_OVERRIDES = {
  assocperformanceview01: "Associate Performance",
  congestionmanagementview01: "Congestion Management",
  dwellstatsview01: "Dwell Stats",
  glidepathview01: "Glide Path",
  inductcontainerview01: "Induct Containers",
  inductjobview01: "Induct Jobs",
  inductstatsview01: "Induct Stats",
  openworkpriorityview01: "Open Work Priority",
  openworkview01: "Open Work",
  orderpoolview01: "Order Pool",
  taskmapview01: "Task Map",
  taskstatsview01: "Task Stats",
  workassignmentview01: "Work Assignment",
};

const METRIC_VALUE_OVERRIDES = {
  APactiveOfTotal: "AP Active Of Total",
  APisActive: "APIs Active",
  APtotal: "AP Total",
};

// Raw column names to omit per view — hides internal, duplicate, and sync-only fields.
const VIEW_COLUMN_EXCLUSIONS = {
  assocperformanceview01: new Set([
    "id",
    "ChangeDatetime",
    "SyncTime",
    "IsActive",
    "ExecUser1",
    "UserID",
    "ExecUserHidden",
    "LastAisleHidden",
    "updatedTime",
    "rowHash",
    "IsDeleted",
    "ExecUser2",
  ]),
  inductjobview01: new Set([
    "hour",
    "hourString",
    "dateTime",
    "ChangeDatetime",
  ]),
};

// Views whose data is rendering-only (spatial coordinates, etc.) and
// should not appear as spreadsheet tabs/files.
const EXCLUDED_VIEWS = new Set(["taskmapview01"]);

/**
 * Build a timestamp string suitable for filenames: YYYYMMDD_HHmmss
 */
function buildTimestamp() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  );
}

function getMetricValueFromRow(row) {
  if (!row || typeof row !== "object") return "";

  for (const columnName of COLLAPSIBLE_VALUE_COLUMNS) {
    const candidate = row[columnName];
    if (candidate === null || candidate === undefined) continue;
    if (typeof candidate === "string" && candidate.trim() === "") continue;
    return candidate;
  }

  return "";
}

function normalizeCellValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim();

  if (Array.isArray(value) || typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

function tokenizeIdentifier(value) {
  return String(value ?? "")
    .trim()
    .replace(/UoM/g, "UOM")
    .replace(/view\d+$/i, " view")
    .replace(/_/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d+)/g, "$1 $2")
    .replace(/(\d+)([A-Za-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

function humanizeIdentifier(value) {
  const tokens = tokenizeIdentifier(value);

  return tokens
    .map((token) => {
      const lower = token.toLowerCase();
      const acronym = ACRONYM_OVERRIDES.get(lower);
      if (acronym) return acronym;
      if (/^[A-Z0-9]+$/.test(token) && token.length <= 4) return token;
      if (/^\d+$/.test(token)) return token;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function getViewLabel(viewName) {
  return VIEW_LABEL_OVERRIDES[viewName] ?? humanizeIdentifier(viewName);
}

function toFilenamePart(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

function getExportColumnLabel(columnName) {
  if (columnName === "Value") return "Value";
  return humanizeIdentifier(columnName);
}

function shouldCollapseValueColumns(rows) {
  return (rows || []).some(
    (row) =>
      row &&
      typeof row === "object" &&
      Array.from(TYPED_VALUE_SOURCE_COLUMNS).some((column) => column in row),
  );
}

function getExportColumns(rows, viewName) {
  if (!rows || rows.length === 0) return [];

  const exclusions = VIEW_COLUMN_EXCLUSIONS[viewName] ?? new Set();
  const rawColumns = Array.from(
    new Set(rows.flatMap((row) => Object.keys(row || {}))),
  ).filter((col) => !exclusions.has(col));
  const collapseValueColumns = shouldCollapseValueColumns(rows);

  if (!collapseValueColumns) return rawColumns;

  const filteredColumns = rawColumns.filter(
    (columnName) => !COLLAPSIBLE_VALUE_COLUMNS.includes(columnName),
  );
  const metricIndex = filteredColumns.indexOf("Metric");
  const valueIndex = metricIndex >= 0 ? metricIndex + 1 : filteredColumns.length;
  filteredColumns.splice(valueIndex, 0, "Value");
  return filteredColumns;
}

function normalizeIdentifierValue(columnName, value) {
  const normalized = normalizeCellValue(value);
  if (typeof normalized !== "string") return normalized;
  if (!IDENTIFIER_VALUE_COLUMNS.has(columnName)) return normalized;
  if (!/^[A-Za-z0-9_]+$/.test(normalized)) return normalized;

  if (columnName === "Metric") {
    const metricOverride = METRIC_VALUE_OVERRIDES[normalized];
    if (metricOverride) return metricOverride;
  }

  return humanizeIdentifier(normalized);
}

function normalizeRowForExport(row, exportColumns, collapseValueColumns) {
  return exportColumns.reduce((accumulator, columnName) => {
    if (columnName === "Value" && collapseValueColumns) {
      accumulator[columnName] = normalizeCellValue(getMetricValueFromRow(row));
      return accumulator;
    }

    accumulator[columnName] = normalizeIdentifierValue(columnName, row?.[columnName]);
    return accumulator;
  }, {});
}

export function prepareViewExport(viewName, rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const exportColumns = getExportColumns(safeRows, viewName);
  const collapseValueColumns = shouldCollapseValueColumns(safeRows);
  const normalizedRows = safeRows.map((row) =>
    normalizeRowForExport(row, exportColumns, collapseValueColumns),
  );
  const headerLabels = exportColumns.map((columnName) =>
    getExportColumnLabel(columnName),
  );
  const data = normalizedRows.map((row) =>
    exportColumns.map((columnName) => row[columnName] ?? ""),
  );
  const viewLabel = getViewLabel(viewName);

  return {
    matrix: [headerLabels, ...data],
    summary: {
      exportedColumns: headerLabels,
      rawColumns: Array.from(
        new Set(safeRows.flatMap((row) => Object.keys(row || {}))),
      ),
      rowCount: safeRows.length,
      sampleRows: normalizedRows.slice(0, 3),
      viewLabel,
      viewName,
    },
    viewLabel,
    worksheetName: viewLabel.replace(/[\\/*?:[\]]/g, "_").slice(0, 31),
    csvFileStem: toFilenamePart(viewLabel),
  };
}

/**
 * Convert an array of row objects to a 2-D array (header row + data rows).
 * Column order is derived from the union of all keys in the rows.
 */
export function exportDashboardCacheAsCsv(cacheByView, prefix = "") {
  const timestamp = buildTimestamp();
  const viewSummaries = [];

  Object.entries(cacheByView || {}).forEach(([viewName, viewData]) => {
    if (EXCLUDED_VIEWS.has(viewName)) return;
    const rows = viewData?.rows ?? [];
    const { matrix, summary, csvFileStem } = prepareViewExport(viewName, rows);
    viewSummaries.push(summary);

    const csvString = matrix
      .map((row) =>
        row
          .map((cell) => {
            const str = cell === null || cell === undefined ? "" : String(cell);
            // Escape cells that contain commas, quotes, or newlines
            if (str.includes(",") || str.includes('"') || str.includes("\n")) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(","),
      )
      .join("\n");

    const fileName = `${prefix ? prefix + "_" : ""}${csvFileStem}_${timestamp}.csv`;
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
}

/**
 * Download a single Excel (.xlsx) file where each view in cacheByView
 * becomes its own worksheet (tab).
 *
 * @param {object} cacheByView   - The cacheByView object from useCacheStreamRouter
 * @param {string} [fileBaseName] - Base filename (without extension)
 */
export function exportDashboardCacheAsExcel(cacheByView, fileBaseName = "dashboard_export") {
  const timestamp = buildTimestamp();
  const wb = XLSX.utils.book_new();
  const viewSummaries = [];

  Object.entries(cacheByView || {}).forEach(([viewName, viewData]) => {
    if (EXCLUDED_VIEWS.has(viewName)) return;
    const rows = viewData?.rows ?? [];
    const { matrix, summary, worksheetName } = prepareViewExport(viewName, rows);
    viewSummaries.push(summary);
    const ws = XLSX.utils.aoa_to_sheet(matrix);
    XLSX.utils.book_append_sheet(wb, ws, worksheetName);
  });

  const fileName = `${fileBaseName}_${timestamp}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

